import is from '@sindresorhus/is';
import { format, isValid } from 'date-fns';
import { useContext, useState } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { TableLoadingSpinner } from '../../../design/components/LoadingSpinner';
import { GeneralDataGrid } from '../../common/data-grids/common/GeneralDataGrid';
import { SkillCell } from '../../common/data-grids/skills/SkillCell';
import { useSkillRelatedJobsQuery } from '../../query-hooks/skill-details';
import { jobPageUrl } from '../job-list/constants';

import type { GlobalSearchJob } from '../../../data-contract/global-search';
import type { GridColDef, GridPaginationModel, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-pro';
import type { FunctionComponent } from 'react';

const formatDate = (date: Date, dateFormat = 'MM/dd/yyyy'): string => (isValid(date) ? format(date, dateFormat) : '');

// eslint-disable-next-line functional/prefer-readonly-type
const useColumns = (skillId: string): GridColDef[] => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { language } = applicationState;

    const columns = [
        {
            disableColumnMenu: true,
            field: 'title',
            flex: 0.4,
            headerName: translate(`pages.skill-details.headlines.job-title`),
            resizable: false,
        },
        {
            disableColumnMenu: true,
            field: 'publishedAt',
            flex: 0.5,
            headerName: translate(`pages.skill-details.headlines.published-at`),
            resizable: false,
            valueFormatter: (value: Date) => formatDate(new Date(value), language.date.format) ?? null,
        },
        {
            disableColumnMenu: true,
            field: 'skills',
            flex: 1,
            headerName: translate(`pages.skill-details.headlines.required-skills`),
            renderCell: (params: GridRenderCellParams<GlobalSearchJob>) => (
                <SkillCell defaultVisibleSkillsCount={2} prioritySkillIds={[skillId]} skills={params.row.skills} />
            ),
            resizable: false,
            sortable: false,
        },
    ];

    return columns;
};

const pageSizeOptions = [5, 10];

const defaultSortModel: GridSortModel = [
    {
        field: 'title',
        sort: 'asc',
    },
];

type JobPostingsProps = {
    readonly skillId: string;
};

export const JobPostings: FunctionComponent<JobPostingsProps> = ({ skillId }) => {
    const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const { actions, translate } = useContext(ApplicationContext);
    const columns = useColumns(skillId);

    const skillRelatedJobsQuery = useSkillRelatedJobsQuery(
        skillId,
        {
            page: paginationModel.page,
            size: paginationModel.pageSize,
        },
        sortModel.map((item) => ({
            direction: item.sort ?? 'asc',
            field: item.field,
        })),
    );

    if (is.undefined(skillRelatedJobsQuery.data)) {
        return null;
    }

    const { data: rows, pagination: paginationData } = skillRelatedJobsQuery.data;

    const noDataMessage = translate('pages.skill-details.messages.no-job-postings');

    return (
        <GeneralDataGrid
            columns={columns}
            disableRowSelectionOnClick={true}
            getRowHeight={() => 'auto'}
            loading={skillRelatedJobsQuery.isPending || skillRelatedJobsQuery.isFetching}
            localeText={{ noRowsLabel: noDataMessage }}
            pageSizeOptions={pageSizeOptions}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            rowCount={paginationData?.count ?? rows.length ?? 0}
            rows={rows ?? []}
            slots={{
                loadingOverlay: TableLoadingSpinner,
            }}
            sortModel={sortModel}
            sx={{
                '& .MuiDataGrid-cell': {
                    alignItems: 'baseline',
                    py: '8px',
                },
                border: 'none',
            }}
            onPaginationModelChange={setPaginationModel}
            onRowClick={(row) => actions.navigateTo(jobPageUrl(row.id.toString()))}
            onSortModelChange={setSortModel}
        />
    );
};
